import { useState, useEffect, useRef } from 'react';
import { Bell, CheckCheck, Building2, CreditCard, Clock, AlertCircle, RefreshCw } from 'lucide-react';
import {
  getAdminNotifications,
  markNotificationRead,
  markAllNotificationsRead,
} from '../../api/adminNotifications';

const EVENT_ICONS = {
  new_company: Building2,
  trial_expiring: Clock,
  trial_expired: AlertCircle,
  payment_success: CreditCard,
  payment_failed: AlertCircle,
  subscription_renewed: RefreshCw,
};

const EVENT_COLORS = {
  new_company: 'text-blue-600 bg-blue-50',
  trial_expiring: 'text-amber-600 bg-amber-50',
  trial_expired: 'text-red-600 bg-red-50',
  payment_success: 'text-green-600 bg-green-50',
  payment_failed: 'text-red-600 bg-red-50',
  subscription_renewed: 'text-blue-600 bg-blue-50',
};

function formatRelative(timestamp) {
  const now = new Date();
  const then = new Date(timestamp);
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffMin < 1) return 'şimdi';
  if (diffMin < 60) return `${diffMin}dk önce`;
  if (diffHour < 24) return `${diffHour}sa önce`;
  if (diffDay < 7) return `${diffDay}g önce`;
  return then.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
}

export function AdminNotificationBell() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const dropdownRef = useRef(null);
  const [actionError, setActionError] = useState('');

  const load = async () => {
    try {
      const d = await getAdminNotifications();
      setNotifications(d.notifications || []);
      setUnread(d.unread || 0);
    } catch (err) {
      console.error('Bildirim yüklenemedi:', err);
    }
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleNotificationClick = async (notif) => {
    if (!notif.is_read) {
      try {
        await markNotificationRead(notif.id);
        setNotifications(prev => prev.map(n =>
          n.id === notif.id ? { ...n, is_read: true } : n
        ));
        setUnread(prev => Math.max(0, prev - 1));
        setActionError('');
      } catch (err) {
        console.error('Bildirim okundu işaretlenemedi:', err);
        setActionError('Okundu işaretlenemedi, tekrar deneyin');
        return;
      }
    }
    setOpen(false);
  };

  const handleReadAll = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnread(0);
      setActionError('');
    } catch (err) {
      console.error('Bildirimler okundu işaretlenemedi:', err);
      setActionError('İşaretlenemedi, tekrar deneyin');
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => { setOpen(!open); setActionError(''); }}
        className="relative p-2 rounded-lg hover:bg-slate-800 transition"
        aria-label="Bildirimler"
      >
        <Bell size={20} className="text-slate-300" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-1">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <h3 className="font-semibold text-slate-800">
              Bildirimler {unread > 0 && <span className="ml-2 text-sm font-normal text-slate-500">({unread} yeni)</span>}
            </h3>
            {unread > 0 && (
              <button
                onClick={handleReadAll}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium"
              >
                <CheckCheck size={14} /> Hepsini okundu işaretle
              </button>
            )}
          </div>

          {actionError && (
            <div className="px-4 py-2 bg-red-50 border-b border-red-100 text-xs text-red-600 flex items-center gap-1.5">
              <AlertCircle size={14} className="flex-shrink-0" /> {actionError}
            </div>
          )}

          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-12 text-center text-sm text-slate-500">
                Henüz bildirim yok
              </div>
            ) : (
              notifications.map(notif => {
                const Icon = EVENT_ICONS[notif.event_type] || Bell;
                const colorClass = EVENT_COLORS[notif.event_type] || 'text-slate-600 bg-slate-50';

                return (
                  <button
                    key={notif.id}
                    onClick={() => handleNotificationClick(notif)}
                    className={`w-full flex items-start gap-3 px-4 py-3 hover:bg-slate-50 transition border-b border-slate-50 text-left ${
                      !notif.is_read ? 'bg-blue-50/30' : ''
                    }`}
                  >
                    <div className={`flex-shrink-0 w-9 h-9 rounded-lg ${colorClass} flex items-center justify-center`}>
                      <Icon size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2 mb-1">
                        <p className={`text-sm ${!notif.is_read ? 'font-semibold text-slate-900' : 'font-medium text-slate-700'}`}>
                          {notif.title}
                        </p>
                        <span className="text-xs text-slate-400 flex-shrink-0">
                          {formatRelative(notif.created_at)}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 line-clamp-2">{notif.body}</p>
                    </div>
                    {!notif.is_read && (
                      <span className="flex-shrink-0 w-2 h-2 rounded-full bg-blue-500 mt-3" />
                    )}
                  </button>
                );
              })
            )}
          </div>

          {notifications.length > 0 && (
            <div className="px-4 py-2 border-t border-slate-100 bg-slate-50">
              <p className="text-xs text-slate-500 text-center">
                Son 50 bildirim gösteriliyor
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
